// Export schedule panel.
//
// Schedules the current export to deliver automatically. Cadence is capped at
// monthly: weekly / daily are never offered, so an export can fire at most
// once a month (the goal's hard constraint). Quarterly and annually sit below
// that ceiling. Recipients, day-of-month and format are captured per schedule;
// existing schedules are listed with their next send date and can be paused or
// removed.
//
// Persistence is localStorage for the prototype (see exportData.js); the real
// surface is `user_exports_schedule` with the same monthly cap enforced in the
// API so a client can't post a faster cadence.
import React, { useState } from 'react';
import { Box, Typography, Button, TextField, Switch, Chip, Tooltip } from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MainCard from '../../../components/cards/MainCard';
import { CADENCES, defaultFirstSend, nextSendAfter, INPUT_SX } from '../exportData';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function CadencePicker({ value, onChange }) {
    return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {CADENCES.map((c) => {
                const active = value === c.id;
                return (
                    <Tooltip key={c.id} title={c.note || ''} disableHoverListener={!c.note}>
                        <Button
                            size="small"
                            onClick={() => onChange(c.id)}
                            sx={{
                                fontSize: 11, textTransform: 'none', minWidth: 0, px: 1.25, py: '3px', borderRadius: '4px',
                                border: '1px solid', borderColor: active ? 'secondary.main' : 'divider',
                                bgcolor: active ? 'secondary.main' : 'transparent',
                                color: active ? 'common.white' : 'text.secondary',
                                '&:hover': { borderColor: 'secondary.main', bgcolor: active ? 'secondary.main' : 'transparent', color: active ? 'common.white' : 'secondary.main' },
                            }}
                        >{c.label}</Button>
                    </Tooltip>
                );
            })}
        </Box>
    );
}

export default function ExportSchedulePanel({ title, format, sectionCount, tableCount = 0, schedules, onSave, onToggle, onDelete }) {
    const [cadence, setCadence] = useState('monthly');
    const [firstSend, setFirstSend] = useState(() => defaultFirstSend());
    const [recipients, setRecipients] = useState('');
    const [error, setError] = useState('');

    const recipientList = recipients.split(/[,\n;]/).map((s) => s.trim()).filter(Boolean);
    const allValid = recipientList.length > 0 && recipientList.every((e) => EMAIL_RE.test(e));
    const today = new Date().toISOString().slice(0, 10);
    const firstSendValid = !!firstSend && firstSend > today;
    const cadenceLabel = (CADENCES.find((c) => c.id === cadence) || {}).label || cadence;

    const handleSave = () => {
        if (sectionCount === 0 && tableCount === 0) { setError('Pick a template with sections or attach at least one data table.'); return; }
        if (!firstSendValid) { setError('Pick a first send date in the future.'); return; }
        if (!allValid) { setError('Enter one or more valid recipient emails.'); return; }
        setError('');
        onSave({ title, format, cadence, firstSend, recipients: recipientList, sectionCount });
        setRecipients('');
    };

    return (
        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
            <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>
                        Automated delivery
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                        Send this export on a schedule. Delivered as <strong>{format}</strong>
                        {tableCount > 0 ? <> with <strong>{tableCount} CSV data table{tableCount === 1 ? '' : 's'}</strong></> : null} to the recipients below.
                    </Typography>
                </Box>
                <Chip
                    icon={<InfoOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                    label="Maximum once a month"
                    size="small"
                    sx={{ fontSize: 10, height: 22, bgcolor: 'action.hover', color: 'text.secondary', '& .MuiChip-icon': { color: 'text.disabled' } }}
                />
            </Box>

            <Box sx={{ p: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
                {/* New schedule */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', mb: 0.75 }}>Cadence</Typography>
                        <CadencePicker value={cadence} onChange={setCadence} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                        <Box sx={{ width: 170 }}>
                            <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', mb: 0.75 }}>First send</Typography>
                            <TextField
                                size="small" type="date" fullWidth
                                value={firstSend}
                                onChange={(e) => setFirstSend(e.target.value)}
                                inputProps={{ min: today }}
                                sx={INPUT_SX}
                            />
                        </Box>
                        <Typography sx={{ fontSize: 11, color: 'text.secondary', pb: 1 }}>
                            {firstSendValid ? <>First send <strong>{firstSend}</strong>, then {cadenceLabel.toLowerCase()}</> : 'Pick a future date'}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', mb: 0.75 }}>Recipients</Typography>
                        <TextField
                            size="small" fullWidth multiline minRows={2}
                            placeholder="board@acme.com, ciso@acme.com"
                            value={recipients}
                            onChange={(e) => setRecipients(e.target.value)}
                            sx={INPUT_SX}
                        />
                        <Typography sx={{ fontSize: 9.5, color: 'text.disabled', mt: 0.5 }}>Comma or line separated. {recipientList.length} recipient{recipientList.length === 1 ? '' : 's'}.</Typography>
                    </Box>
                    <Button
                        variant="contained" color="secondary" size="small"
                        onClick={handleSave}
                        startIcon={<ScheduleIcon sx={{ fontSize: 14 }} />}
                        sx={{ textTransform: 'none', fontSize: 12, alignSelf: 'flex-start' }}
                    >
                        Schedule {cadence} delivery
                    </Button>
                    {error && <Typography sx={{ fontSize: 10.5, color: 'error.main' }}>{error}</Typography>}
                </Box>

                {/* Existing schedules */}
                <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', mb: 0.75 }}>
                        Active schedules ({schedules.length})
                    </Typography>
                    {schedules.length === 0 ? (
                        <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1, p: 2, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>No scheduled exports yet.</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 240, overflowY: 'auto' }}>
                            {schedules.map((s) => {
                                const cad = CADENCES.find((c) => c.id === s.cadence);
                                return (
                                    <Box key={s.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                        <Box sx={{ flex: 1, minWidth: 0, opacity: s.enabled ? 1 : 0.5 }}>
                                            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.title}</Typography>
                                            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                                                {cad?.label || s.cadence} · {s.format} · next {s.nextSend}
                                                {s.tables?.length ? ` · +${s.tables.length} CSV table${s.tables.length === 1 ? '' : 's'}` : ''}
                                            </Typography>
                                            <Typography sx={{ fontSize: 9.5, color: 'text.disabled', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {(s.recipients || []).join(', ')}
                                            </Typography>
                                        </Box>
                                        <Tooltip title={s.enabled ? 'Pause' : 'Resume'}>
                                            <Switch size="small" checked={!!s.enabled} onChange={() => onToggle(s.id)} color="secondary" />
                                        </Tooltip>
                                        <Tooltip title="Remove schedule">
                                            <Button onClick={() => onDelete(s.id)} sx={{ minWidth: 0, p: 0.5, color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                                                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                            </Button>
                                        </Tooltip>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </Box>
        </MainCard>
    );
}
